/**
 * State machine for managing scenario decision graphs
 * Handles node transitions, consequence flags, and decision tracking
 */

import type { SessionState, DecisionRecord, Phase } from './types';
import type {
  Scenario,
  StateNode,
  DecisionEdge,
  FeedbackContent,
  DelayedConsequence,
} from '@data/types';
import { eventBus } from './EventBus';

const STORAGE_KEY = 'safepassage_session';

export class StateMachine {
  private currentScenario: Scenario | null = null;
  private sessionState: SessionState;
  private decisionStartTime: number = 0;

  constructor() {
    this.sessionState = this.loadSession() || this.createNewSession();
  }

  /**
   * Create a fresh session state
   */
  private createNewSession(): SessionState {
    return {
      currentPhase: 1,
      currentScenarioIndex: 0,
      currentNodeId: '',
      activeConsequenceFlags: [],
      decisionHistory: [],
      selectedRole: null,
      completedScenarios: [],
      startedAt: Date.now(),
      lastUpdatedAt: Date.now(),
    };
  }

  /**
   * Load session from localStorage
   */
  private loadSession(): SessionState | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored) as SessionState;
      }
    } catch (error) {
      console.warn('Failed to load session:', error);
    }
    return null;
  }

  /**
   * Save session to localStorage
   */
  private saveSession(): void {
    try {
      this.sessionState.lastUpdatedAt = Date.now();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.sessionState));
    } catch (error) {
      console.warn('Failed to save session:', error);
    }
  }

  /**
   * Get current session state
   */
  getSessionState(): Readonly<SessionState> {
    return { ...this.sessionState };
  }

  /**
   * Reset session to initial state
   */
  resetSession(): void {
    this.sessionState = this.createNewSession();
    this.currentScenario = null;
    this.saveSession();
    eventBus.emit('session:start');
  }

  /**
   * Set the selected role
   */
  setRole(role: SessionState['selectedRole']): void {
    this.sessionState.selectedRole = role;
    this.saveSession();
  }

  /**
   * Load a scenario into the state machine
   */
  loadScenario(scenario: Scenario): StateNode {
    this.currentScenario = scenario;
    this.sessionState.currentNodeId = scenario.initialNodeId;
    this.sessionState.activeConsequenceFlags = [];
    this.saveSession();

    const initialNode = this.getCurrentNode();
    if (!initialNode) {
      throw new Error(`Initial node ${scenario.initialNodeId} not found in scenario`);
    }

    return initialNode;
  }

  /**
   * Get the current scenario
   */
  getCurrentScenario(): Scenario | null {
    return this.currentScenario;
  }

  /**
   * Get the current state node
   */
  getCurrentNode(): StateNode | null {
    if (!this.currentScenario) return null;
    return (
      this.currentScenario.nodes.find(
        (node) => node.id === this.sessionState.currentNodeId
      ) || null
    );
  }

  /**
   * Get available edges from the current node
   */
  getAvailableEdges(): DecisionEdge[] {
    if (!this.currentScenario) return [];
    return this.currentScenario.edges.filter(
      (edge) => edge.fromNodeId === this.sessionState.currentNodeId
    );
  }

  /**
   * Start timing for a decision
   */
  startDecisionTimer(): void {
    this.decisionStartTime = Date.now();
  }

  /**
   * Make a decision and transition to the next state
   * Returns the edge taken and any triggered delayed consequences
   */
  makeDecision(decisionId: string): {
    edge: DecisionEdge;
    nextNode: StateNode;
    triggeredConsequences: Array<{
      consequence: DelayedConsequence;
      feedback: FeedbackContent;
    }>;
  } | null {
    if (!this.currentScenario) return null;

    // Find the edge for this decision
    const edge = this.currentScenario.edges.find(
      (e) =>
        e.fromNodeId === this.sessionState.currentNodeId &&
        e.decisionId === decisionId
    );

    if (!edge) {
      console.warn(`No edge found for decision ${decisionId} from node ${this.sessionState.currentNodeId}`);
      return null;
    }

    // Record the decision
    const record: DecisionRecord = {
      scenarioId: this.currentScenario.id,
      nodeId: this.sessionState.currentNodeId,
      decisionId,
      timestamp: Date.now(),
      timeToDecide: this.decisionStartTime > 0 ? Date.now() - this.decisionStartTime : 0,
    };
    this.sessionState.decisionHistory.push(record);

    // Handle delayed consequence flag
    if (edge.delayedConsequence) {
      this.sessionState.activeConsequenceFlags.push(edge.delayedConsequence.flagId);
    }

    // Transition to next node
    this.sessionState.currentNodeId = edge.toNodeId;
    const nextNode = this.getCurrentNode();

    if (!nextNode) {
      throw new Error(`Next node ${edge.toNodeId} not found in scenario`);
    }

    // Add any consequence flags from entering this node
    nextNode.consequenceFlags.forEach((flag) => {
      if (!this.sessionState.activeConsequenceFlags.includes(flag)) {
        this.sessionState.activeConsequenceFlags.push(flag);
      }
    });

    // Check for triggered delayed consequences
    const triggeredConsequences: Array<{
      consequence: DelayedConsequence;
      feedback: FeedbackContent;
    }> = [];

    // Find all delayed consequences that trigger at this node
    this.currentScenario.edges.forEach((e) => {
      if (
        e.delayedConsequence &&
        e.delayedConsequence.triggerNodeId === nextNode.id &&
        this.sessionState.activeConsequenceFlags.includes(e.delayedConsequence.flagId)
      ) {
        triggeredConsequences.push({
          consequence: e.delayedConsequence,
          feedback: e.delayedConsequence.feedback,
        });
      }
    });

    this.saveSession();
    eventBus.emit('decision:made', { decisionId, edge, nextNode });

    if (triggeredConsequences.length > 0) {
      eventBus.emit('consequence:triggered', { consequences: triggeredConsequences });
    }

    return { edge, nextNode, triggeredConsequences };
  }

  /**
   * Mark current scenario as complete
   */
  completeScenario(): void {
    if (this.currentScenario) {
      if (!this.sessionState.completedScenarios.includes(this.currentScenario.id)) {
        this.sessionState.completedScenarios.push(this.currentScenario.id);
      }
      this.sessionState.currentScenarioIndex++;
      this.saveSession();
    }
  }

  /**
   * Advance to next phase
   */
  advancePhase(): boolean {
    if (this.sessionState.currentPhase < 3) {
      this.sessionState.currentPhase = (this.sessionState.currentPhase + 1) as Phase;
      this.sessionState.currentScenarioIndex = 0;
      this.saveSession();
      eventBus.emit('phase:complete', { phase: this.sessionState.currentPhase - 1 });
      return true;
    }
    return false;
  }

  /**
   * Check if a consequence flag is active
   */
  hasConsequenceFlag(flagId: string): boolean {
    return this.sessionState.activeConsequenceFlags.includes(flagId);
  }

  /**
   * Get decision history for analysis
   */
  getDecisionHistory(): ReadonlyArray<DecisionRecord> {
    return [...this.sessionState.decisionHistory];
  }

  /**
   * Clear decision history (for testing or reset)
   */
  clearHistory(): void {
    this.sessionState.decisionHistory = [];
    this.sessionState.activeConsequenceFlags = [];
    this.saveSession();
  }
}

// Export singleton instance
export const stateMachine = new StateMachine();
